import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip, BarChart, Bar, XAxis, YAxis } from 'recharts';
import { ResourceSummary } from '../../services/awsService';

interface ResourcePieChartsProps {
  summary: ResourceSummary;
}

const COLORS = {
  ec2: '#3B82F6', // Blue-500
  eks: '#1E40AF', // Blue-800
  ecs: '#60A5FA', // Blue-400
  rds: '#6B7280', // Gray-500
  ebs: '#4B5563', // Gray-600
  s3: '#9CA3AF', // Gray-400
  fsx: '#374151', // Gray-700
  efs: '#1F2937', // Gray-800
  dynamodb: '#D1D5DB', // Gray-300
  cloudwatchLogs: '#E5E7EB', // Gray-200
};

const ResourcePieCharts: React.FC<ResourcePieChartsProps> = ({ summary }) => {
  // Calculate compute by service (vCPUs)
  const computeData = [];
  
  if (summary.ec2.totalVcpus > 0) {
    computeData.push({
      name: 'EC2',
      value: summary.ec2.totalVcpus,
      color: COLORS.ec2
    });
  }
  
  if (summary.eks.totalVcpus > 0) {
    computeData.push({
      name: 'EKS',
      value: summary.eks.totalVcpus,
      color: COLORS.eks
    });
  }
  
  // ECS doesn't have vCPU data in the summary, but we can add it if available
  // For now, we'll skip it unless the data structure is updated
  
  if (summary.rds?.totalVcpus && summary.rds.totalVcpus > 0) {
    computeData.push({
      name: 'RDS',
      value: summary.rds.totalVcpus,
      color: COLORS.rds
    });
  }

  // Calculate memory by service (GB)
  const memoryData = [];
  
  if (summary.ec2.totalMemory > 0) {
    memoryData.push({
      name: 'EC2',
      value: Math.round(summary.ec2.totalMemory),
      color: COLORS.ec2
    });
  }
  
  if (summary.eks.totalMemory > 0) {
    memoryData.push({
      name: 'EKS',
      value: Math.round(summary.eks.totalMemory),
      color: COLORS.eks
    });
  }
  
  if (summary.rds?.totalMemory && summary.rds.totalMemory > 0) {
    memoryData.push({
      name: 'RDS',
      value: Math.round(summary.rds.totalMemory),
      color: COLORS.rds
    });
  }

  // Calculate storage by service (GB)
  const storageData = [];
  
  if (summary.ebs.totalGiB > 0) {
    storageData.push({
      name: 'EBS',
      value: summary.ebs.totalGiB,
      color: COLORS.ebs
    });
  }
  
  if (summary.s3.totalGiB > 0) {
    storageData.push({
      name: 'S3',
      value: summary.s3.totalGiB,
      color: COLORS.s3
    });
  }
  
  if (summary.rds?.totalStorageGB && summary.rds.totalStorageGB > 0) {
    storageData.push({
      name: 'RDS',
      value: summary.rds.totalStorageGB,
      color: COLORS.rds
    });
  }
  
  if (summary.fsx?.totalStorageGB && summary.fsx.totalStorageGB > 0) {
    storageData.push({
      name: 'FSx',
      value: summary.fsx.totalStorageGB,
      color: COLORS.fsx
    });
  }
  
  if (summary.efs?.totalGiB && summary.efs.totalGiB > 0) {
    storageData.push({
      name: 'EFS',
      value: summary.efs.totalGiB,
      color: COLORS.efs
    });
  }
  
  if (summary.dynamodb?.totalGiB && summary.dynamodb.totalGiB > 0) {
    storageData.push({
      name: 'DynamoDB',
      value: summary.dynamodb.totalGiB,
      color: COLORS.dynamodb
    });
  }
  
  if (summary.cloudwatchLogs?.totalStoredGiB && summary.cloudwatchLogs.totalStoredGiB > 0) {
    storageData.push({
      name: 'CloudWatch Logs',
      value: summary.cloudwatchLogs.totalStoredGiB,
      color: COLORS.cloudwatchLogs
    });
  }

  const CustomTooltip = ({ active, payload, label, isStorage, isMemory }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      const unit = isStorage || isMemory ? 'GB' : 'vCPUs';
      return (
        <div className="bg-gray-900 dark:bg-gray-950 p-3 rounded-lg shadow-xl border border-gray-700 dark:border-gray-800">
          <p className="font-semibold text-white">{data.name || data.payload?.name}</p>
          <p className="text-sm text-gray-300">
            {(data.value || data.payload?.value || 0).toLocaleString()} {unit}
          </p>
          {data.payload?.total && (
            <p className="text-xs text-gray-400">
              {((data.value / data.payload.total) * 100).toFixed(1)}% of total
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  const renderCustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null; // Don't show label for small slices
    
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="white" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        className="text-xs font-medium"
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Add total to data for percentage calculation
  const computeTotal = computeData.reduce((sum, item) => sum + item.value, 0);
  const memoryTotal = memoryData.reduce((sum, item) => sum + item.value, 0);
  const storageTotal = storageData.reduce((sum, item) => sum + item.value, 0);
  
  computeData.forEach(item => (item as any).total = computeTotal);
  memoryData.forEach(item => (item as any).total = memoryTotal);
  storageData.forEach(item => (item as any).total = storageTotal);

  if (computeData.length === 0 && storageData.length === 0 && memoryData.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
        Resource Distribution
      </h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Compute by Service */}
        {computeData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
              Compute by Service (vCPUs)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={computeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {computeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip isStorage={false} isMemory={false} />} />
                  <Legend 
                    formatter={(value, entry: any) => (
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {value} ({entry.payload.value} vCPUs)
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total: {computeTotal.toLocaleString()} vCPUs
              </p>
            </div>
          </div>
        )}

        {/* Memory by Service - Bar Chart */}
        {memoryData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
              Memory by Service (GB)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={memoryData} 
                  layout="horizontal"
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <XAxis 
                    dataKey="name" 
                    tick={{ fill: '#9CA3AF' }}
                    axisLine={{ stroke: '#4B5563' }}
                  />
                  <YAxis 
                    tick={{ fill: '#9CA3AF' }}
                    axisLine={{ stroke: '#4B5563' }}
                  />
                  <Tooltip 
                    content={<CustomTooltip isStorage={false} isMemory={true} />}
                  />
                  <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                    {memoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total: {memoryTotal.toLocaleString()} GB
              </p>
            </div>
          </div>
        )}

        {/* Storage by Service */}
        {storageData.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
              Storage Used by Service (GB)
            </h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={storageData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={renderCustomLabel}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {storageData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip isStorage={true} isMemory={false} />} />
                  <Legend 
                    formatter={(value, entry: any) => (
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {value} ({entry.payload.value.toLocaleString()} GB)
                      </span>
                    )}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Total: {storageTotal.toLocaleString()} GB
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourcePieCharts;